import React, {useCallback, useMemo, useReducer, useState} from 'react';
import type {MeshType} from 'api/generated';
import {ModelItem, Button, TextWithLine, Transitioner, Modal, ModelViewer} from 'components';
import {produce} from 'immer';

import useProjectStore from '../../stores/useProjectStore';

interface ISidebarRightAssetsProps {}

const SidebarRightAssets = ({}: ISidebarRightAssetsProps) => {
  const meshes = useProjectStore(s => s.meshes) ?? [];
  const unassignMeshes = useProjectStore(s => s.unassignMeshes);

  const [showModel, toggleModel] = useReducer(s => !s, false);
  const [toUnassign, setToUnassign] = useState<MeshType[]>([]);
  const [toShow, setToShow] = useState<MeshType>();

  const handleUnassignFromProject = () => {
    unassignMeshes(toUnassign);
    setToUnassign([]);
  };

  const handleClicked = useCallback((mesh: MeshType) => {
    setToUnassign(
      produce(draft => {
        const index = draft.findIndex(m => m.uid === mesh.uid);
        if (index === -1) {
          draft.unshift(mesh);
        } else {
          draft.splice(index, 1);
        }
      }),
    );
  }, []);

  const handleShow = useCallback((mesh: MeshType) => {
    setToShow(mesh);
    toggleModel();
  }, []);

  const assignedMeshes = useMemo(() => {
    return meshes.filter(m => m.assigned);
  }, [meshes]);

  return (
    <Transitioner fromY={-20} delay={0.1}>
      <div className="flex justify-between items-center">
        <h2 className="dark:text-white">Assigned Models</h2>
      </div>
      <div className="my-3">
        <TextWithLine text="" />
      </div>

      <div className="flex items-center justify-between">
        {toUnassign.length === 0 ? <span className="text-sm/6">Files</span> : <span className="text-sm/6">{toUnassign.length} selected</span>}
        <div className="w-28.5 flex flex-col whitespace-nowrap">
          <Button text="Remove models" size="extra-small" variant="alert" onClick={handleUnassignFromProject} disabled={toUnassign.length === 0} />
        </div>
      </div>
      <p className="mt-3 mb-2 text-xs text-slate-500 dark:text-darkText">Select models to remove from this project.</p>

      <Modal visible={showModel} onClose={toggleModel}>
        {!!toShow && <ModelViewer mesh={toShow} />}
      </Modal>

      <Transitioner fromY={-15} delay={0.2} classNames="py-3">
        <ul className="mt-2 -m-1.5 grid grid-cols-2 gap-y-1">
          {assignedMeshes.map(mesh => {
            const checked = !!toUnassign.find(s => s.uid === mesh.uid);
            return <ModelItem key={mesh.uid} mesh={mesh} onClick={handleClicked} onShow={handleShow} readOnly checked={checked} />;
          })}
        </ul>
      </Transitioner>
    </Transitioner>
  );
};

export default React.memo(SidebarRightAssets);
